import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import API from "../api.js";
import { CATEGORIES } from "../config/constants.js";
import { toast } from "react-toastify";
import ImageSlider from "../components/ImageSlider.jsx";

function ProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);

  const user = JSON.parse(localStorage.getItem("user")) || null;


  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const { data } = await API.get(`/products/${id}`);
        const currentProduct = data.data;

        setProduct(currentProduct);
        setEditData(currentProduct);
      } catch (err) {
        toast.error("Error loading product.");
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
    window.scrollTo(0, 0);
  }, [id]);


  const CATEGORY_COLORS = {
    Electronics: "text-blue-500",
    Vehicles: "text-red-500",
    Furniture: "text-yellow-500",
    default: "text-green-500",
  };

  const getCategoryColor = (category) =>
    CATEGORY_COLORS[category] || CATEGORY_COLORS.default;


  const validateEditData = () => {
    if (!editData.title?.trim()) {
      toast.error("Title is required");
      return false;
    }

    if (!editData.price || editData.price < 1) {
      toast.error("Price must be greater than ₹1");
      return false;
    }

    const wordCount =
      editData.description?.trim().split(/\s+/).filter(Boolean).length || 0;

    if (wordCount < 10) {
      toast.error("Description must contain at least 10 words.");
      return false;
    }

    if (wordCount > 100) {
      toast.error("Description cannot exceed 100 words.");
      return false;
    }

    return true;
  };


  const handleEdit = async () => {
    if (!validateEditData()) return;

    setIsUpdating(true);

    try {
      const res = await API.put(`/products/${id}`, {
        title: editData.title.trim(),
        price: Number(editData.price),
        description: editData.description.trim(),
        category: editData.category,
      });

      setProduct(res.data.data);
      setIsEditing(false);
      toast.success("Product updated successfully!");
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to update product"
      );
    } finally {
      setIsUpdating(false);
    }
  };


  const handleDelete = async () => {
    const confirmDelete = window.confirm("Delete this listing?");
    if (!confirmDelete) return;

    try {
      await API.delete(`/products/${id}`);
      toast.success("Product deleted!");
      navigate("/");
    } catch (err) {
      toast.error("Delete failed.");
    }
  };


  const handleContact = () => {
    const phoneNumber =
      product?.phoneNumber || product?.seller?.phone;

    const cleanNumber = phoneNumber?.replace(/\D/g, "");

    if (!cleanNumber || cleanNumber.length !== 10) {
      toast.error("Invalid seller contact number.");
      return;
    }

    const message = encodeURIComponent(
      `Hi, I'm interested in your listing: ${product.title} on Ramtek Bazar.`
    );

    window.open(
      `https://wa.me/91${cleanNumber}?text=${message}`,
      "_blank"
    );
  };


  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: product.title,
          text: product.description,
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success("Link copied to clipboard!");
      }
    } catch {}
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-blue-500 text-xl font-bold animate-pulse">
          Loading...
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white">
        Product not found.
      </div>
    );
  }


  const loggedInId = user?._id ?? user?.id;
  const sellerId = product.seller?._id || product.seller;

  const isOwner =
    loggedInId &&
    sellerId &&
    String(loggedInId) === String(sellerId);


  return (
    <div className="min-h-screen bg-slate-950 p-6">
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 bg-slate-900 p-8 rounded-3xl border border-slate-800">

        <div className="aspect-square bg-slate-950 rounded-2xl border border-slate-800 overflow-hidden">
          <ImageSlider images={product.images} />
        </div>

        <div>

          {isEditing ? (
            <select
              value={editData.category}
              onChange={(e) =>
                setEditData({ ...editData, category: e.target.value })
              }
              className="bg-slate-950 border border-blue-500 text-white px-4 py-2 rounded-xl"
            >
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          ) : (
            <span
              className={`text-sm font-bold uppercase ${getCategoryColor(
                product.category
              )}`}
            >
              {product.category}
            </span>
          )}

          {isEditing ? (
            <input
              className="w-full bg-slate-950 border border-blue-500 rounded-xl px-4 py-2 mt-4 text-white text-3xl font-bold"
              value={editData.title}
              onChange={(e) =>
                setEditData({ ...editData, title: e.target.value })
              }
            />
          ) : (
            <h1 className="text-4xl font-bold text-white mt-4">
              {product.title}
            </h1>
          )}

          {isEditing ? (
            <input
              type="number"
              min="1"
              className="bg-slate-950 border border-blue-500 rounded-xl px-4 py-2 text-white text-2xl mt-4"
              value={editData.price}
              onChange={(e) =>
                setEditData({
                  ...editData,
                  price: Number(e.target.value),
                })
              }
            />
          ) : (
            <p className="text-3xl text-white mt-4">
              {new Intl.NumberFormat("en-IN", {
                style: "currency",
                currency: "INR",
                maximumFractionDigits: 0,
              }).format(product.price)}
            </p>
          )}

          <div className="mt-6">
            <p className="text-slate-400 text-sm">Seller</p>

            <Link
              to={`/seller/${sellerId}`}
              className="text-blue-400 font-semibold hover:underline"
            >
              {product.seller?.name || "View Seller Profile"}
            </Link>
          </div>

          {isEditing ? (
            <div className="mt-6">
              <textarea
                maxLength={800}
                className="w-full bg-slate-950 border border-blue-500 rounded-xl px-4 py-3 text-white"
                value={editData.description}
                onChange={(e) =>
                  setEditData({
                    ...editData,
                    description: e.target.value,
                  })
                }
              />
              <p className="text-xs text-slate-400 mt-2 text-right">
                {editData.description?.trim().split(/\s+/).filter(Boolean).length || 0} / 100 words
              </p>
            </div>
          ) : (
            <p className="text-slate-300 mt-6 whitespace-pre-wrap">
              {product.description}
            </p>
          )}

          <div className="mt-8 flex gap-6">
            <button
              onClick={handleContact}
              className="text-green-500 font-semibold"
            >
              Chat via WhatsApp
            </button>

            <button
              onClick={handleShare}
              className="text-slate-300 font-semibold"
            >
              Share
            </button>
          </div>

          {isOwner && (
            <div className="flex gap-3 mt-6">
              {isEditing ? (
                <>
                  <button
                    onClick={handleEdit}
                    disabled={isUpdating}
                    className="bg-green-600 px-4 py-2 rounded-xl text-white disabled:opacity-50"
                  >
                    {isUpdating ? "Saving..." : "Save"}
                  </button>

                  <button
                    onClick={() => setIsEditing(false)}
                    className="bg-slate-700 px-4 py-2 rounded-xl text-white"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="bg-yellow-500 text-black px-4 py-2 rounded-xl"
                  >
                    Edit Listing
                  </button>

                  <button
                    onClick={handleDelete}
                    className="bg-red-600 text-white px-4 py-2 rounded-xl"
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default ProductDetails;